/**
* @file 框架-顶部-右侧操作按钮
* @description 包含搜索菜单、帮助中心、司机注册、APP下载等功能
* @version 1.2.0
* */
<template>
  <div class="header-right flex">
    <!-- 心跳状态指示灯 -->
    <el-tooltip :content="heartbeatTooltip" placement="bottom" :open-delay="300">
      <div class="heartbeat-indicator" @click="showHeartbeatDetail">
        <span class="heartbeat-dot" :class="'status-' + heartbeatStatus"></span>
      </div>
    </el-tooltip>

    <!-- 搜索菜单 -->
    <div class="menu-search-wrapper">
      <el-popover
          placement="bottom-end"
          width="420"
          trigger="click"
          v-model="searchVisible"
          popper-class="menu-search-popover"
          :visible-arrow="false">
        <div class="search-content" style="padding: 12px;box-sizing: border-box">
          <div class="search-header">
            <i class="el-icon-search"></i>
            <span>快速导航</span>
            <kbd class="shortcut-key">Ctrl+K</kbd>
          </div>
          <el-input
              ref="menuSearchInput"
              v-model="menuSearchText"
              placeholder="输入菜单名称搜索..."
              prefix-icon="el-icon-search"
              size="medium"
              clearable
              class="search-input"
              @input="handleMenuSearch"
              @keydown.enter.native="goToFirstResult"
              @keydown.esc.native="closeSearch"
              @keydown.down.native.prevent="navigateResults('down')"
              @keydown.up.native.prevent="navigateResults('up')">
          </el-input>
          <div class="search-results" v-if="menuSearchText">
            <div v-if="searchResults.length === 0" class="no-results">
              <div class="no-results-icon">
                <i class="el-icon-search"></i>
              </div>
              <span>未找到匹配的菜单</span>
              <p class="no-results-tip">试试其他关键词</p>
            </div>
            <div v-else class="results-list">
              <div class="results-count">找到 {{ searchResults.length }} 个结果</div>
              <div
                  v-for="(item, index) in searchResults"
                  :key="item.id"
                  class="result-item"
                  :class="{ 'is-active': activeIndex === index }"
                  @click="goToMenu(item)"
                  @mouseenter="activeIndex = index">
                <div class="result-icon">
                  <i class="el-icon-document"></i>
                </div>
                <div class="result-info">
                  <span class="result-name" v-html="highlightKeyword(item.menuName)"></span>
                  <span class="result-path" v-if="item.parentName">
                    <i class="el-icon-folder"></i>
                    {{ item.parentName }}
                  </span>
                </div>
                <div class="result-action">
                  <i class="el-icon-right"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="search-tips" v-else>
            <!-- 最近访问放在前面 -->
            <div class="recent-menus" v-if="recentMenus.length > 0">
              <div class="recent-title">最近访问</div>
              <div
                  v-for="item in recentMenus"
                  :key="item.id"
                  class="recent-item"
                  @click="goToMenu(item)">
                <i class="el-icon-time"></i>
                <span>{{ item.menuName }}</span>
              </div>
            </div>
            <!-- 快捷键提示放在底部，更紧凑 -->
            <div class="tips-footer">
              <span class="tip-item"><kbd>↑</kbd><kbd>↓</kbd> 选择</span>
              <span class="tip-item"><kbd>Enter</kbd> 跳转</span>
              <span class="tip-item"><kbd>Esc</kbd> 关闭</span>
            </div>
          </div>
        </div>
        <div slot="reference" class="operation-btn flex has-bg search-btn" :class="{ 'is-active': searchVisible }">
          <div class="btn-icon">
            <i class="el-icon-search"></i>
          </div>
          <span>搜索</span>
          <kbd class="search-shortcut">⌘K</kbd>
        </div>
      </el-popover>
    </div>

    <!-- 帮助中心 -->
    <el-dropdown size="medium" @command="helpFn" v-if="menuPreFn(72)">
      <div class="operation-btn flex has-bg help-btn">
        <div class="btn-icon">
          <i class="el-icon-question"></i>
        </div>
        <span>帮助中心</span>
      </div>
      <el-dropdown-menu slot="dropdown">
        <el-dropdown-item command="used" v-if="mixinUserInfo.platformType !== 3">使用手册</el-dropdown-item>
        <el-dropdown-item command="logs">更新日志</el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>
    <div class="operation-btn flex has-bg help-btn" @click="getUrl" v-else>
      <div class="btn-icon">
        <i class="el-icon-question"></i>
      </div>
      <span>帮助中心</span>
    </div>

    <!-- 司机注册 -->
    <div
        class="operation-btn flex has-bg driver-register-btn"
        v-if="mixinUserInfo.platformType === 3 && mixinUserInfo.type !== 3"
        @click="$emit('show-qrcode')"
    >
      <div class="btn-icon">
        <i class="el-icon-user-solid"></i>
      </div>
      <span>司机注册</span>
    </div>

    <!-- APP下载 -->
    <div class="operation-btn flex has-bg app-download-btn" @click="$emit('app-download')">
      <div class="btn-icon">
        <i class="el-icon-mobile-phone"></i>
      </div>
      <span>APP下载</span>
    </div>

    <!-- 退票窗口 -->
    <div
        class="operation-btn flex has-bg refund-btn"
        v-if="menuPreFn(78)"
        @click="$router.push('/orderReturn')"
    >
      <div class="btn-icon">
        <i class="el-icon-refresh-left"></i>
      </div>
      <span>退票窗口</span>
    </div>

    <!-- 平台下单/窗口售票 -->
    <div
        class="operation-btn flex has-bg order-btn"
        v-if="menuPreFn(31)"
        @click="orderFormOpen"
    >
      <div class="btn-icon">
        <i class="el-icon-edit-outline"></i>
      </div>
      <span>{{ mixinUserInfo.platformType == 3 ? '代客下单' : mixinUserInfo.platformType == 1 ? '平台下单' : '窗口售票' }}</span>
    </div>

    <!-- 商品预约 -->
    <div
        class="operation-btn flex has-bg goods-btn"
        v-if="menuPreFn(125)"
        @click="$emit('goods-open')"
    >
      <div class="btn-icon">
        <i class="el-icon-goods"></i>
      </div>
      <span>商品预约</span>
    </div>

    <el-dropdown trigger="click" size="medium" @command="logout">
      <div class="operation-btn flex user-info-btn" style="margin: 0 0 0 10px;">
        <div class="user-avatar-wrap">
          <img v-if="userAvatar" :src="mixinQiniuImg(userAvatar)" class="user-avatar-img" @error="userAvatar = null" />
          <span v-else class="user-avatar-letter">{{ mixinUserInfo && mixinUserInfo.name ? mixinUserInfo.name.charAt(0) : '?' }}</span>
        </div>
        <span class="user-name">{{ mixinUserInfo && mixinUserInfo.name ? mixinUserInfo.name : "" }}</span>
        <i class="el-icon-arrow-down el-icon--right" style="margin-left:2px"></i>
      </div>
      <el-dropdown-menu slot="dropdown">
        <el-dropdown-item command="个人中心"><i class="el-icon-user" style="margin-right:6px"></i>个人中心</el-dropdown-item>
        <el-dropdown-item command="修改密码"><i class="el-icon-lock" style="margin-right:6px"></i>修改密码</el-dropdown-item>
        <el-dropdown-item divided command="退出登录"><i class="el-icon-switch-button" style="margin-right:6px;color:#f56c6c"></i>退出登录</el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>

    <!--帮助中心弹窗-->
    <el-dialog
        :title="helpDialogTitle"
        :visible.sync="helpDialogVisible"
        width="90%"
        top="2vh"
        :close-on-click-modal="false"
        class="help-center-dialog"
        append-to-body
    >
      <div class="iframe-container">
        <iframe
            :src="helpDialogUrl"
            frameborder="0"
            width="100%"
            height="100%"
            allowfullscreen
        ></iframe>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="helpDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import externalLinks, {getManualUrl} from '@/assets/json/externalLinks'

export default {
  name: 'HeaderRight',
  data() {
    return {
      heartbeatStatus: 'unknown',
      heartbeatDetail: {},
      heartbeatTimer: null,
      searchVisible: false,
      menuSearchText: '',
      searchResults: [],
      menuList: [],
      activeIndex: 0,
      recentMenus: [],
      // 帮助中心弹窗
      helpDialogVisible: false,
      helpDialogUrl: '',
      helpDialogTitle: '帮助中心',
      // 用户头像
      userAvatar: null
    }
  },
  computed: {
    heartbeatTooltip() {
      if (this.heartbeatStatus === 'ok') return '系统正常 · MQTT已连接'
      if (this.heartbeatStatus === 'warning') return '部分异常 · MQTT部分断开'
      if (this.heartbeatStatus === 'error') return '连接异常 · MQTT已断开'
      return '检测中...'
    }
  },
  created() {
    // 加载最近访问的菜单
    this.loadRecentMenus()
    // 注册全局快捷键
    document.addEventListener('keydown', this.handleGlobalShortcut)
    // 启动心跳检测
    this.checkHeartbeat()
    this.heartbeatTimer = setInterval(() => this.checkHeartbeat(), 30000)
    // 加载用户头像
    this.loadUserAvatar()
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleGlobalShortcut)
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
  },
  watch: {
    searchVisible(val) {
      if (val) {
        this.$nextTick(() => {
          this.$refs.menuSearchInput && this.$refs.menuSearchInput.focus()
        })
        this.loadMenuList()
        this.activeIndex = 0
      } else {
        this.menuSearchText = ''
        this.searchResults = []
        this.activeIndex = 0
      }
    },
    menuSearchText() {
      this.activeIndex = 0
    }
  },
  methods: {
    async loadMenuList() {
      // 从 sessionStorage 获取菜单数据，或从 API 获取
      try {
        const url = "/api/v1/manage/menu/permission"
        const {code, data} = await this.$axios.get(url)
        if (code === 1 && data.menu) {
          this.menuList = this.flattenMenu(data.menu)
        }
      } catch (error) {
        console.error('获取菜单失败', error)
      }
    },
    flattenMenu(menus, parentName = '') {
      let result = []
      menus.forEach(item => {
        if (item.children && item.children.length > 0) {
          result = result.concat(this.flattenMenu(item.children, item.menuName))
        } else {
          result.push({
            ...item,
            parentName
          })
        }
      })
      return result
    },
    handleGlobalShortcut(e) {
      // Ctrl+K 或 Cmd+K 打开搜索
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        this.searchVisible = !this.searchVisible
      }
    },
    loadRecentMenus() {
      try {
        const recent = localStorage.getItem('recentMenus')
        if (recent) {
          this.recentMenus = JSON.parse(recent).slice(0, 5)
        }
      } catch (e) {
        this.recentMenus = []
      }
    },
    saveRecentMenu(item) {
      try {
        let recent = []
        const stored = localStorage.getItem('recentMenus')
        if (stored) {
          recent = JSON.parse(stored)
        }
        // 移除已存在的相同菜单
        recent = recent.filter(m => m.id !== item.id)
        // 添加到开头
        recent.unshift({id: item.id, menuName: item.menuName, path: item.path})
        // 只保留最近10个
        recent = recent.slice(0, 10)
        localStorage.setItem('recentMenus', JSON.stringify(recent))
        this.recentMenus = recent.slice(0, 5)
      } catch (e) {
        console.error('保存最近菜单失败', e)
      }
    },
    handleMenuSearch() {
      if (!this.menuSearchText) {
        this.searchResults = []
        return
      }
      const keyword = (this.menuSearchText || '').toLowerCase()
      this.searchResults = this.menuList.filter(item =>
          (item.menuName && item.menuName.toLowerCase().includes(keyword)) ||
          (item.parentName && item.parentName.toLowerCase().includes(keyword))
      ).slice(0, 10)
    },
    highlightKeyword(text) {
      if (!this.menuSearchText || !text) return text
      const keyword = this.menuSearchText
      const regex = new RegExp(`(${keyword})`, 'gi')
      return text.replace(regex, '<mark>$1</mark>')
    },
    navigateResults(direction) {
      if (this.searchResults.length === 0) return
      if (direction === 'down') {
        this.activeIndex = (this.activeIndex + 1) % this.searchResults.length
      } else {
        this.activeIndex = (this.activeIndex - 1 + this.searchResults.length) % this.searchResults.length
      }
    },
    goToMenu(item) {
      if (item.path) {
        this.$router.push(item.path)
        sessionStorage.setItem('menuActive', item.id)
        this.saveRecentMenu(item)
        this.closeSearch()
      }
    },
    goToFirstResult() {
      if (this.searchResults.length > 0) {
        this.goToMenu(this.searchResults[this.activeIndex])
      }
    },
    closeSearch() {
      this.searchVisible = false
      this.menuSearchText = ''
      this.searchResults = []
      this.activeIndex = 0
    },
    helpFn(val) {
      if (val === "used") {
        this.getUrl()
      } else {
        this.viewUpdateLogs()
      }
    },
    getUrl() {
      const url = getManualUrl(this.mixinUserInfo.platformType)
      this.helpDialogTitle = '使用手册'
      this.helpDialogUrl = url
      this.helpDialogVisible = true
    },
    viewUpdateLogs() {
      this.helpDialogTitle = '更新日志'
      this.helpDialogUrl = externalLinks.changelog
      this.helpDialogVisible = true
    },
    orderFormOpen() {
      if (this.mixinUserInfo.platformType == 3) {
        this.$router.push('/orderFormHitchRide')
      } else if (this.mixinUserInfo.platformType == 1) {
        this.$router.push('/orderForm')
      } else {
        this.$router.push('/orderFormByChannel3')
      }
    },
    async loadUserAvatar() {
      try {
        const { code, data } = await this.$axios.get('/api/v1/manage/admin/my/profile')
        if (code === 1 && data && data.avatar) {
          this.userAvatar = data.avatar
        }
      } catch (e) {
        // 头像加载失败不影响主流程
      }
    },
    async checkHeartbeat() {
      try {
        const data = await this.$axios.get('/api/v1/common/heartbeat')
        if (data && data.code === 1 && data.data) {
          this.heartbeatStatus = data.data.status || 'error'
          this.heartbeatDetail = data.data
        } else {
          this.heartbeatStatus = 'error'
        }
      } catch (e) {
        this.heartbeatStatus = 'error'
      }
    },
    showHeartbeatDetail() {
      const d = this.heartbeatDetail
      const pub = d.mqttPubConnected ? '正常' : '断开'
      const sub = d.mqttSubConnected ? '正常' : '断开'
      this.$alert(
        `<div style="line-height:2">`
        + `<p><b>MQTT推送：</b><span style="color:${d.mqttPubConnected ? '#67C23A' : '#F56C6C'}">${pub}</span></p>`
        + `<p><b>MQTT订阅：</b><span style="color:${d.mqttSubConnected ? '#67C23A' : '#F56C6C'}">${sub}</span></p>`
        + `<p><b>机器码：</b>${d.machineId || '-'}</p>`
        + `<p><b>服务器时间：</b>${d.serverTimestamp ? new Date(d.serverTimestamp).toLocaleString() : '-'}</p>`
        + `</div>`,
        '系统状态',
        { dangerouslyUseHTMLString: true, confirmButtonText: '确定' }
      )
    },
    logout(e) {
      if (e === "退出登录") {
        this.$store.dispatch("acLogout")
      } else if (e === "个人中心") {
        this.$router.push('/myProfile')
      } else {
        this.$emit('change-password')
      }
    }
  }
}
</script>

<style lang="less" scoped>
//搜索
.search-input{
  width: 35rem;
}
.header-right {
  flex: 1;
  justify-content: flex-end;
  text-align: left;
  font-style: normal;

  .menu-search-wrapper {
    margin-right: 10px;
  }

  .operation-btn {
    height: 48px;
    font-family: SourceHanSansSC, SourceHanSansSC;
    font-weight: 400;
    font-size: 14px;
    cursor: pointer;
    color: #FFFFFF;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 20px;
    transition: all 0.25s ease;
    position: relative;

    &:hover {
      color: #ffffffEE;
    }

    img {
      width: 16px;
      height: 16px;
      margin-right: 4px;
    }


    // 带背景的按钮通用样式
    &.has-bg {
      padding: 4px 10px;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.06) 100%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      height: 32px;

      .btn-icon {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 6px;
        transition: all 0.3s ease;

        i {
          font-size: 11px;
          color: #fff;
        }
      }

      &:hover {
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%);
        border-color: rgba(255, 255, 255, 0.2);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

        .btn-icon {
          transform: scale(1.1);
        }
      }
    }

    // 司机注册按钮
    &.driver-register-btn {
      .btn-icon {
        background: linear-gradient(135deg, #FF6633 0%, #FF8855 100%);
      }

      &:hover .btn-icon {
        box-shadow: 0 4px 12px rgba(255, 102, 51, 0.4);
      }
    }

    // APP下载按钮
    &.app-download-btn {
      .btn-icon {
        background: linear-gradient(135deg, #7EBF50 0%, #9AD06A 100%);
      }

      &:hover .btn-icon {
        box-shadow: 0 4px 12px rgba(126, 191, 80, 0.4);
      }
    }

    // 退票窗口按钮
    &.refund-btn {
      .btn-icon {
        background: linear-gradient(135deg, #E6A23C 0%, #F0B85A 100%);
      }

      &:hover .btn-icon {
        box-shadow: 0 4px 12px rgba(230, 162, 60, 0.4);
      }
    }

    // 平台下单按钮
    &.order-btn {
      .btn-icon {
        background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%);
      }

      &:hover .btn-icon {
        box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
      }
    }

    // 商品预约按钮
    &.goods-btn {
      .btn-icon {
        background: linear-gradient(135deg, #67C23A 0%, #85CE61 100%);
      }

      &:hover .btn-icon {
        box-shadow: 0 4px 12px rgba(103, 194, 58, 0.4);
      }
    }

    // 帮助中心按钮
    &.help-btn {
      .btn-icon {
        background: linear-gradient(135deg, #909399 0%, #A6A9AD 100%);
      }

      &:hover .btn-icon {
        box-shadow: 0 4px 12px rgba(144, 147, 153, 0.4);
      }
    }

    // 搜索按钮
    &.search-btn {
      .btn-icon {
        background: linear-gradient(135deg, #606266 0%, #909399 100%);
      }

      .search-shortcut {
        margin-left: 6px;
        padding: 1px 5px;
        background: rgba(255, 255, 255, 0.15);
        border-radius: 4px;
        font-size: 10px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        opacity: 0.7;
      }

      &:hover .btn-icon {
        box-shadow: 0 4px 12px rgba(96, 98, 102, 0.4);
      }

      &.is-active {
        background: linear-gradient(135deg, rgba(64, 158, 255, 0.2) 0%, rgba(64, 158, 255, 0.1) 100%);
        border-color: rgba(64, 158, 255, 0.4);

        .btn-icon {
          background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%);
        }
      }
    }

    // 用户信息按钮（头像+姓名）
    &.user-info-btn {
      padding: 4px 10px;
      border-radius: 20px;
      gap: 8px;
      border: 1px solid rgba(255, 255, 255, 0.15);
      transition: all 0.25s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.12);
        border-color: rgba(255, 255, 255, 0.25);
      }

      .user-avatar-wrap {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
        background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1.5px solid rgba(255, 255, 255, 0.4);

        .user-avatar-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .user-avatar-letter {
          color: #fff;
          font-size: 13px;
          font-weight: 600;
          line-height: 1;
        }
      }

      .user-name {
        font-size: 14px;
        font-weight: 500;
        color: #fff;
        max-width: 80px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
}

// 心跳指示灯
.heartbeat-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: 10px;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .heartbeat-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: -3px;
      left: -3px;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      opacity: 0.3;
    }

    &.status-ok {
      background: #67C23A;
      box-shadow: 0 0 6px rgba(103, 194, 58, 0.6);
      &::after { background: #67C23A; }
    }

    &.status-warning {
      background: #E6A23C;
      box-shadow: 0 0 6px rgba(230, 162, 60, 0.6);
      animation: heartbeat-pulse 2s infinite;
      &::after { background: #E6A23C; }
    }

    &.status-error {
      background: #F56C6C;
      box-shadow: 0 0 6px rgba(245, 108, 108, 0.6);
      animation: heartbeat-pulse 1s infinite;
      &::after { background: #F56C6C; }
    }

    &.status-unknown {
      background: #909399;
      &::after { background: #909399; }
    }
  }
}

@keyframes heartbeat-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

// 手机振动动画
@keyframes phone-ring {
  0%, 100% {
    transform: scale(1.1) rotate(0deg);
  }
  20% {
    transform: scale(1.1) rotate(-10deg);
  }
  40% {
    transform: scale(1.1) rotate(10deg);
  }
  60% {
    transform: scale(1.1) rotate(-10deg);
  }
  80% {
    transform: scale(1.1) rotate(10deg);
  }
}
</style>

<!-- 全局样式（搜索弹窗） -->
<style lang="less">
.menu-search-popover {
  padding: 0 !important;
  border: none !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  overflow: hidden;

  .search-content {
    padding: 0;
    background: #fff;
    min-width: 400px;

    .search-header {
      display: flex;
      align-items: center;
      padding: 16px 20px 12px;
      background: #fff;
      border-bottom: 1px solid #f0f0f0;

      i {
        font-size: 16px;
        color: #409EFF;
        margin-right: 10px;
      }

      span {
        font-size: 15px;
        font-weight: 600;
        color: #303133;
      }

      .shortcut-key {
        margin-left: auto;
        padding: 3px 10px;
        background: #f5f7fa;
        border: 1px solid #e4e7ed;
        border-radius: 4px;
        font-size: 12px;
        color: #909399;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      }
    }

    .el-input {
      padding: 16px 20px;
      background: #fff;
   /*   width: 35rem;*/
      .el-input__inner {
        border-radius: 8px;
        border: 1px solid #dcdfe6;
        padding-left: 40px;
        height: 44px;
        font-size: 14px;
        transition: all 0.2s ease;

        &:focus {
          border-color: #409EFF;
          box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
          &::placeholder {
            display: none;
          }
        }

        &::placeholder {
          color: #c0c4cc;
        }
      }

      .el-input__prefix {
        top: 5%;
        left: 2.5rem;
        color: #c0c4cc;
        font-size: 16px;
      }
    }

    .search-results {
      max-height: 300px;
      overflow-y: auto;

      .results-count {
        padding: 8px 20px;
        font-size: 12px;
        color: #909399;
        background: #fafafa;
        border-bottom: 1px solid #f0f0f0;
      }

      .results-list {
        padding: 8px 0;
      }

      .result-item {
        display: flex;
        align-items: center;
        padding: 10px 20px;
        cursor: pointer;
        transition: all 0.15s ease;

        &:hover, &.is-active {
          background: #f5f7fa;
        }

        &.is-active {
          .result-icon {
            background: #409EFF;

            i {
              color: #fff;
            }
          }

          .result-action i {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .result-icon {
          width: 28px;
          height: 28px;
          border-radius: 6px;
          background: #e4e7ed;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 10px;
          transition: all 0.15s ease;

          i {
            font-size: 13px;
            color: #909399;
          }
        }

        .result-info {
          flex: 1;
          min-width: 0;

          .result-name {
            display: block;
            font-size: 13px;
            color: #303133;
            font-weight: 500;

            mark {
              background: #fef08a;
              color: #854d0e;
              padding: 0 2px;
              border-radius: 2px;
            }
          }

          .result-path {
            display: flex;
            align-items: center;
            font-size: 11px;
            color: #909399;
            margin-top: 2px;

            i {
              font-size: 10px;
              margin-right: 4px;
            }
          }
        }

        .result-action {
          i {
            font-size: 13px;
            color: #409EFF;
            opacity: 0;
            transform: translateX(-4px);
            transition: all 0.15s ease;
          }
        }
      }

      .no-results {
        padding: 32px 16px;
        text-align: center;

        .no-results-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #f5f7fa;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 10px;

          i {
            font-size: 20px;
            color: #c0c4cc;
          }
        }

        span {
          display: block;
          font-size: 13px;
          color: #909399;
          font-weight: 500;
        }

        .no-results-tip {
          font-size: 12px;
          color: #c0c4cc;
          margin-top: 4px;
        }
      }
    }

    .search-tips {
      padding: 12px 20px;
      background: #fafafa;
      border-top: 1px solid #f0f0f0;

      .recent-menus {
        margin-bottom: 12px;

        .recent-title {
          font-size: 12px;
          color: #909399;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }

        .recent-item {
          display: flex;
          align-items: center;
          padding: 8px 12px;
          margin: 0 -8px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s ease;

          &:hover {
            background: #fff;
          }

          i {
            font-size: 14px;
            color: #c0c4cc;
            margin-right: 8px;
          }

          span {
            font-size: 13px;
            color: #606266;
          }
        }
      }

      .tips-footer {
        display: flex;
        justify-content: center;
        gap: 16px;
        padding-top: 12px;
        border-top: 1px solid #ebeef5;

        .tip-item {
          font-size: 12px;
          color: #909399;

          kbd {
            padding: 2px 6px;
            background: #fff;
            border: 1px solid #e4e7ed;
            border-radius: 4px;
            font-size: 11px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            margin-right: 3px;
          }
        }
      }
    }
  }
}

// 帮助中心弹窗样式
.help-center-dialog {
  /deep/ .el-dialog__body {
    padding: 0;
    height: 95vh;
  }

  .iframe-container {
    width: 100%;
    height: 100%;

    iframe {
      width: 100%;
      height: 100%;
      min-height: 95vh;
      border: none;
    }
  }
}
</style>
